import { useState } from "preact/hooks"
import { Download, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { GlobalFilterState, type DateKind } from "@/global_filter"
import { toast } from "@/components/ui/use-toast"
import { download_export, map, toISODateString } from "@/utils"
import { TrendViewState } from "./trend_view_state"
import type { Granularity } from "./yoy_trend"

export interface DashboardExportParams {
  /** Global filter bar — what the summary cards are showing. */
  startDate?: string
  endDate?: string
  distributor?: string | null
  dateKind: DateKind
  /** Deduction Trends — its own window and granularity. */
  trendStartDate?: string
  trendEndDate?: string
  granularity: Granularity
}

/**
 * Builds the export URL. Only the filters that are actually set are sent, so an unfiltered
 * dashboard exports as "all time" rather than as an empty range.
 */
export function build_export_url(params: DashboardExportParams): string {
  const query = new URLSearchParams()
  if (params.startDate) query.append("start_date", params.startDate)
  if (params.endDate) query.append("end_date", params.endDate)
  if (params.distributor) query.append("distributor", params.distributor)
  query.append("date_kind", params.dateKind)
  if (params.trendStartDate) query.append("trend_start_date", params.trendStartDate)
  if (params.trendEndDate) query.append("trend_end_date", params.trendEndDate)
  query.append("granularity", params.granularity)
  return `/api/deductions/dashboard-export?${query}`
}

/** Fallback used only when the server sends no `Content-Disposition` filename. */
export function fallback_filename(params: DashboardExportParams): string {
  if (params.startDate && params.endDate) {
    return `dashboard_${params.startDate}_${params.endDate}.xlsx`
  }
  return "dashboard.xlsx"
}

/** Downloads the summary cards and the Deduction Trends chart as one workbook. */
export function DashboardExportButton({ className }: { className?: string }) {
  const [startDate, endDate, distributor, dateKind] = GlobalFilterState.use(s => [
    s.startDate,
    s.endDate,
    s.distributor,
    s.dateKind,
  ])
  const trend = TrendViewState.use()
  const [exporting, setExporting] = useState(false)

  async function handle_export() {
    const params: DashboardExportParams = {
      startDate: map(startDate, toISODateString),
      endDate: map(endDate, toISODateString),
      distributor,
      dateKind,
      trendStartDate: trend.startDate || undefined,
      trendEndDate: trend.endDate || undefined,
      granularity: trend.granularity,
    }
    setExporting(true)
    try {
      await download_export(build_export_url(params), fallback_filename(params))
    } catch (error) {
      console.error("Failed to export the dashboard:", error)
      toast({
        variant: "destructive",
        title: "Export failed",
        description: "Could not export the dashboard. Please try again.",
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <Button variant="outline" size="sm" onClick={handle_export} disabled={exporting} className={className}>
      {exporting ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Download className="h-4 w-4 mr-2" />
      )}
      {exporting ? "Exporting…" : "Export"}
    </Button>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  const base: DashboardExportParams = {
    startDate: "2026-07-23",
    endDate: "2026-09-21",
    distributor: null,
    dateKind: "check",
    trendStartDate: "2023-01-01",
    trendEndDate: "2026-09-21",
    granularity: "year",
  }

  describe("build_export_url", () => {
    it("sends the summary filters and the chart's own window", () => {
      const url = new URL(build_export_url(base), "https://example.com")
      expect(url.pathname).toBe("/api/deductions/dashboard-export")
      expect(url.searchParams.get("start_date")).toBe("2026-07-23")
      expect(url.searchParams.get("end_date")).toBe("2026-09-21")
      expect(url.searchParams.get("date_kind")).toBe("check")
      expect(url.searchParams.get("trend_start_date")).toBe("2023-01-01")
      expect(url.searchParams.get("granularity")).toBe("year")
    })

    it("omits an unset filter instead of sending an empty one", () => {
      // An unfiltered dashboard means "all time" — a blank start_date would be a parse error.
      const url = new URL(
        build_export_url({ ...base, startDate: undefined, endDate: undefined, trendStartDate: undefined }),
        "https://example.com"
      )
      expect(url.searchParams.has("start_date")).toBe(false)
      expect(url.searchParams.has("end_date")).toBe(false)
      expect(url.searchParams.has("trend_start_date")).toBe(false)
      expect(url.searchParams.has("distributor")).toBe(false)
      expect(url.searchParams.get("date_kind")).toBe("check")
    })

    it("carries the selected customer", () => {
      const url = new URL(build_export_url({ ...base, distributor: "kehe" }), "https://example.com")
      expect(url.searchParams.get("distributor")).toBe("kehe")
    })

    it("carries a drilled-in range and its monthly granularity", () => {
      const url = new URL(
        build_export_url({ ...base, trendStartDate: "2025-04-01", trendEndDate: "2025-06-30", granularity: "month" }),
        "https://example.com"
      )
      expect(url.searchParams.get("trend_start_date")).toBe("2025-04-01")
      expect(url.searchParams.get("trend_end_date")).toBe("2025-06-30")
      expect(url.searchParams.get("granularity")).toBe("month")
    })
  })

  describe("fallback_filename", () => {
    it("names the filtered range", () => {
      expect(fallback_filename(base)).toBe("dashboard_2026-07-23_2026-09-21.xlsx")
    })

    it("falls back when the dashboard is unfiltered", () => {
      expect(fallback_filename({ ...base, startDate: undefined, endDate: undefined })).toBe("dashboard.xlsx")
    })
  })
}
